import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";

export const ParticipantRatingUnreadContext = createContext(null);

const clampInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

export const formatUnreadBadge = (count) => {
  const n = clampInt(count);
  if (n <= 0) return "";
  if (n > 999) return "999+";
  return String(n);
};

const EMPTY_CONTEXT = {
  unreadCount: 0,
  scorePercent: null,
  formatUnreadBadge,
  refetch: async () => {},
};

export const ParticipantRatingUnreadProvider = ({
  children,
  enabled = true,
  intervalMs = 60_000,
}) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refresh = () => {
      queryClient.invalidateQueries({
        queryKey: ["participant_rating_unread_summary"],
      });
    };

    window.addEventListener("vg-participant-rating-unread-refresh", refresh);
    window.addEventListener("focus", refresh);

    return () => {
      window.removeEventListener("vg-participant-rating-unread-refresh", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [queryClient]);

  const { data, refetch } = useQuery({
    queryKey: ["participant_rating_unread_summary"],
    enabled: !!enabled,
    queryFn: async () => {
      const res = await runBackgroundRequest(() => (
        api.get("/events/participant-rating/unread-summary/", {
          meta: { suppressGlobalError: true },
        })
      ));
      return res?.data || { unread_changes_count: 0 };
    },
    refetchInterval: intervalMs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const value = useMemo(
    () => ({
      unreadCount: clampInt(data?.unread_changes_count),
      scorePercent: Number.isFinite(Number(data?.score_percent))
        ? Number(data?.score_percent)
        : null,
      formatUnreadBadge,
      refetch,
    }),
    [data?.score_percent, data?.unread_changes_count, refetch]
  );

  return (
    <ParticipantRatingUnreadContext.Provider value={value}>
      {children}
    </ParticipantRatingUnreadContext.Provider>
  );
};

export const useParticipantRatingUnread = () => {
  const ctx = useContext(ParticipantRatingUnreadContext);
  return ctx || EMPTY_CONTEXT;
};
